import { makeAutoObservable } from 'mobx'
import { ViewColumnTemplateFragment } from 'types/graphql'

export class TableViewColumnTemplateStore {
  private _columnTemplates: ViewColumnTemplateFragment[]

  constructor(args: { columnTemplates: ViewColumnTemplateFragment[] }) {
    this._columnTemplates = args.columnTemplates
    makeAutoObservable(this)
  }

  public addColumnTemplate(columnTemplate: ViewColumnTemplateFragment) {
    this._columnTemplates.push(columnTemplate)
  }

  private _findIndexOfColumnTemplate(columnTemplate: ViewColumnTemplateFragment): number {
    return this._columnTemplates.findIndex((ct) => ct.generatedId === columnTemplate.generatedId)
  }

  public deleteColumnTemplate(columnTemplate: ViewColumnTemplateFragment) {
    const index = this._findIndexOfColumnTemplate(columnTemplate)
    if (index < 0) {
      return
    }
    this._columnTemplates.splice(index, 1)
  }

  public getColumnTemplateByGeneratedId(
    generatedId: string
  ): ViewColumnTemplateFragment | undefined {
    return this._columnTemplates.find((ct) => ct.generatedId === generatedId)
  }

  public replaceColumnTemplate(columnTemplate: ViewColumnTemplateFragment) {
    const index = this._findIndexOfColumnTemplate(columnTemplate)
    if (index < 0) {
      return
    }
    this._columnTemplates.splice(index, 1, columnTemplate)
  }

  /**
   * Applies a server refetch without dropping session-only templates
   * (e.g. a VCCT just created via mutation before the catalog query
   * refetches). Updates matching ids in place; appends new server ids.
   */
  public mergeFromServer(serverTemplates: ViewColumnTemplateFragment[]) {
    const serverById = new Map(serverTemplates.map((t) => [t.generatedId, t]))
    for (let i = 0; i < this._columnTemplates.length; i++) {
      const current = this._columnTemplates[i]
      if (!current) {
        continue
      }
      const fromServer = serverById.get(current.generatedId)
      if (fromServer) {
        this._columnTemplates[i] = fromServer
        serverById.delete(current.generatedId)
      }
    }
    for (const template of serverById.values()) {
      this._columnTemplates.push(template)
    }
  }

  public reset() {
    this._columnTemplates = []
  }

  public get columnTemplates(): ViewColumnTemplateFragment[] {
    return this._columnTemplates
  }
}
